/* eslint-disable react/destructuring-assignment */
import React from "react";
import { Flex } from "@xometry/xometry_loft";
import CuttingPlaneController from "./CuttingPlaneController";
import {
  CuttingPlaneButton,
  HomeIcon,
  IconButton,
  IconCamera,
  IconCameraBack,
  IconCameraBottom,
  IconCameraFront,
  IconCameraIso,
  IconCameraLeft,
  IconCameraOrtho,
  IconCameraPersp,
  IconCameraRight,
  IconCameraTop,
  IconCuttingPlane,
  IconHiddenLine,
  // IconMeasurementToolsEdgeLength,
  // IconMeasurementToolsFaceToFaceAngle,
  // IconMeasurementToolsFaceToFaceDistance,
  // IconRemoveAllMeasurements,
  IconShaded,
  IconWireframe,
  IconWireframeShaded,
  NavItem,
  Popover,
  PopoverArrow,
  StyledToolbar,
  SubmenuRowTitle,
} from "./Toolbar.styled";

interface Props {
  viewer: Communicator.WebViewer;
  initialized: boolean;
}

interface State {
  viewOrientationDuration: number;
  cameraSubmenuOpen: boolean;
  wireframeSubmenuOpen: boolean;
  measurementToolsSubmenuOpen: boolean;
  cuttingPlaneSubmenuOpen: boolean;
  selectedMeasurementTool: Communicator.OperatorId;
  cuttingPlane: {
    Controller: CuttingPlaneController | null;
    submenu: {
      open: boolean;
      button: {
        planeX: IconButton;
        planeY: IconButton;
        planeZ: IconButton;
        visibility: IconButton;
        section: IconButton;
        reset: IconButton;
      };
    };
  };
}

class Toolbar extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      viewOrientationDuration: 500,
      cameraSubmenuOpen: false,
      wireframeSubmenuOpen: false,
      measurementToolsSubmenuOpen: false,
      cuttingPlaneSubmenuOpen: false,
      selectedMeasurementTool: Communicator.OperatorId.None,
      cuttingPlane: {
        Controller: null,
        submenu: {
          open: false,
          button: {
            planeX: {
              selected: false,
              inverted: false,
            },
            planeY: {
              selected: false,
              inverted: false,
            },
            planeZ: {
              selected: false,
              inverted: false,
            },
            visibility: {
              selected: false,
              disabled: true,
            },
            section: {
              selected: false,
              disabled: true,
            },
            reset: {
              disabled: true,
            },
          },
        },
      },
    };

    if (props.viewer) {
      this.state.cuttingPlane.Controller = new CuttingPlaneController(
        props.viewer
      );
    }
  }

  homeAction = () => {
    const { viewer, initialized } = this.props;
    if (!viewer) {
      return;
    }
    if (viewer.model.isDrawing()) {
      const sheetId = viewer.sheetManager.getActiveSheetId();
      if (sheetId !== null) {
        viewer.view.isolateNodes([sheetId]);
      }
    } else if (initialized) {
      viewer.view.setViewOrientation(
        Communicator.ViewOrientation.Iso,
        this.state.viewOrientationDuration
      );
      viewer.noteTextManager?.setIsolateActive(false);
      viewer.noteTextManager?.updatePinVisibility();

      const handleOperator = viewer.operatorManager.getOperator(
        Communicator.OperatorId.Handle
      );
      if (handleOperator !== null && handleOperator.removeHandles) {
        handleOperator.removeHandles();
      }
    }
  };

  setMode = mode => {
    const { viewer } = this.props;
    if (!viewer) {
      return;
    }
    const { view } = viewer;
    switch (mode) {
      case "shaded":
        return view.setDrawMode(Communicator.DrawMode.Shaded);
      case "wireframe":
        return view.setDrawMode(Communicator.DrawMode.Wireframe);
      case "hiddenLine":
        return view.setDrawMode(Communicator.DrawMode.HiddenLine);
      case "xray":
        return view.setDrawMode(Communicator.DrawMode.XRay);
      default:
      case "wireframeShaded":
        return view.setDrawMode(Communicator.DrawMode.WireframeOnShaded);
    }
  };

  toggleSubmenu = group => {
    switch (group) {
      case "camera":
        this.setState(prevState => ({
          cameraSubmenuOpen: !prevState.cameraSubmenuOpen,
          wireframeSubmenuOpen: false,
          cuttingPlaneSubmenuOpen: false,
          measurementToolsSubmenuOpen: false,
        }));
        break;
      case "wireframe":
        this.setState(prevState => ({
          wireframeSubmenuOpen: !prevState.wireframeSubmenuOpen,
          cameraSubmenuOpen: false,
          cuttingPlaneSubmenuOpen: false,
          measurementToolsSubmenuOpen: false,
        }));
        break;
      case "cuttingPlane":
        this.setState(prevState => ({
          cuttingPlaneSubmenuOpen: !prevState.cuttingPlaneSubmenuOpen,
          cameraSubmenuOpen: false,
          wireframeSubmenuOpen: false,
          measurementToolsSubmenuOpen: false,
        }));
        break;
      case "measurementTools":
        this.setState(prevState => ({
          measurementToolsSubmenuOpen: !prevState.measurementToolsSubmenuOpen,
          cameraSubmenuOpen: false,
          cuttingPlaneSubmenuOpen: false,
          wireframeSubmenuOpen: false,
        }));
        break;
      default:
    }
  };

  cuttingPlaneButtonClick = (planeAction: string) => {
    if (!this.state.cuttingPlane.Controller) {
      return null;
    }

    const initialCount = this.state.cuttingPlane.Controller?.getCount() || 0;
    let promise: Promise<void>;
    if (
      planeAction === "x" ||
      planeAction === "y" ||
      planeAction === "z" ||
      planeAction === "face"
    ) {
      promise = this.state.cuttingPlane.Controller.toggle(planeAction).then(
        () => {
          const s = { ...this.state };
          const count = this.state.cuttingPlane.Controller?.getCount() || 0;

          let element: IconButton | null = null;
          switch (planeAction) {
            case "x":
              element = this.state.cuttingPlane.submenu.button.planeX;
              break;
            case "y":
              element = this.state.cuttingPlane.submenu.button.planeY;
              break;
            case "z":
              element = this.state.cuttingPlane.submenu.button.planeZ;
              break;
            default:
          }

          if (element) {
            element.selected = false;
            element.inverted = false;
            if (count > initialCount) {
              element.selected = true;
            } else if (
              count === initialCount &&
              count > 0 &&
              planeAction !== "face"
            ) {
              element.inverted = true;
            }
          }

          if (count > 0) {
            s.cuttingPlane.submenu.button.visibility.disabled = false;
            s.cuttingPlane.submenu.button.reset.disabled = false;
          } else {
            s.cuttingPlane.submenu.button.visibility.disabled = true;
            s.cuttingPlane.submenu.button.reset.disabled = true;
          }
          s.cuttingPlane.submenu.button.section.disabled = count <= 1;
          this.setState(s);
        }
      );
    } else if (planeAction === "visibility") {
      // toggle between showing and hiding reference geometry
      promise =
        this.state.cuttingPlane.Controller.toggleReferenceGeometry().then(
          () => {
            const s = { ...this.state };
            const selectedState =
              this.state.cuttingPlane.submenu.button.visibility.selected;
            s.cuttingPlane.submenu.button.visibility.selected = !selectedState;
            this.setState(s);
          }
        );
    } else if (planeAction === "section") {
      // toggle between cutting planes in a single cutting sections and separate cutting sections
      promise = this.state.cuttingPlane.Controller.toggleCuttingMode().then(
        () => {
          const s = { ...this.state };
          const selectedState =
            this.state.cuttingPlane.submenu.button.section.selected;
          s.cuttingPlane.submenu.button.section.selected = !selectedState;
          this.setState(s);
        }
      );
    } else if (planeAction === "reset") {
      // remove and reset all cutting planes
      promise = this.state.cuttingPlane.Controller.resetCuttingPlanes().then(
        () => {
          const s = { ...this.state };
          s.cuttingPlane.submenu.button = {
            planeX: {
              selected: false,
              inverted: false,
            },
            planeY: {
              selected: false,
              inverted: false,
            },
            planeZ: {
              selected: false,
              inverted: false,
            },
            visibility: {
              selected: false,
              disabled: true,
            },
            section: {
              selected: false,
              disabled: true,
            },
            reset: {
              disabled: true,
            },
          };
          this.setState(s);
        }
      );
    } else {
      promise = Promise.resolve();
    }
    return promise;
  };

  selectMeasurementTool = (
    viewer: Communicator.WebViewer,
    tool: Communicator.OperatorId
  ) => {
    if (tool === this.state.selectedMeasurementTool) {
      this.setState(
        {
          selectedMeasurementTool: Communicator.OperatorId.None,
        },
        () => {
          console.log(
            "none",
            viewer.operatorManager.push(Communicator.OperatorId.None)
          );
        }
      );
    } else {
      this.setState(
        {
          selectedMeasurementTool: tool,
        },
        () => {
          console.log(tool, viewer.operatorManager.push(tool));
        }
      );
    }
  };

  render() {
    const { viewer } = this.props;

    if (!viewer) {
      return null;
    }

    const cuttingPlaneBtn = this.state.cuttingPlane.submenu.button;

    return (
      <StyledToolbar>
        <NavItem>
          <HomeIcon onClick={this.homeAction} title="Reset To Original View" />
        </NavItem>
        <NavItem>
          <IconCamera
            id="popoverCamera"
            onClick={() => this.toggleSubmenu("camera")}
            title="View"
          />
          {this.state.cameraSubmenuOpen && (
            <Popover index={1}>
              <PopoverArrow top="98px" />
              <SubmenuRowTitle>Projection Mode</SubmenuRowTitle>
              <Flex>
                <IconCameraOrtho
                  onClick={() => {
                    viewer.view.setProjectionMode(
                      Communicator.Projection.Orthographic
                    );
                  }}
                  title="Parallel"
                />
                <IconCameraPersp
                  onClick={() => {
                    viewer.view.setProjectionMode(
                      Communicator.Projection.Perspective
                    );
                  }}
                  title="Perspective"
                />
              </Flex>
              <SubmenuRowTitle>View</SubmenuRowTitle>
              <Flex>
                <IconCameraIso
                  onClick={() => {
                    viewer.view.setViewOrientation(
                      Communicator.ViewOrientation.Iso,
                      this.state.viewOrientationDuration
                    );
                  }}
                  title="Isometric"
                />
                <IconCameraLeft
                  onClick={() => {
                    viewer.view.setViewOrientation(
                      Communicator.ViewOrientation.Left,
                      this.state.viewOrientationDuration
                    );
                  }}
                  title="Left"
                />
                <IconCameraRight
                  onClick={() => {
                    viewer.view.setViewOrientation(
                      Communicator.ViewOrientation.Right,
                      this.state.viewOrientationDuration
                    );
                  }}
                  title="Right"
                />
              </Flex>
              <Flex>
                <IconCameraBottom
                  onClick={() => {
                    viewer.view.setViewOrientation(
                      Communicator.ViewOrientation.Bottom,
                      this.state.viewOrientationDuration
                    );
                  }}
                  title="Bottom"
                />
                <IconCameraFront
                  onClick={() => {
                    viewer.view.setViewOrientation(
                      Communicator.ViewOrientation.Front,
                      this.state.viewOrientationDuration
                    );
                  }}
                  title="Front"
                />
                <IconCameraBack
                  onClick={() => {
                    viewer.view.setViewOrientation(
                      Communicator.ViewOrientation.Back,
                      this.state.viewOrientationDuration
                    );
                  }}
                  title="Back"
                />
              </Flex>
              <Flex>
                <IconCameraTop
                  onClick={() => {
                    viewer.view.setViewOrientation(
                      Communicator.ViewOrientation.Top,
                      this.state.viewOrientationDuration
                    );
                  }}
                  title="Top"
                />
              </Flex>
            </Popover>
          )}
        </NavItem>
        <NavItem>
          <IconWireframe
            id="popoverWireframe"
            onClick={() => this.toggleSubmenu("wireframe")}
            title="Model Display"
          />
          {this.state.wireframeSubmenuOpen && (
            <Popover index={2}>
              <PopoverArrow top="70px" />
              <IconWireframeShaded
                onClick={() =>
                  viewer.view.setDrawMode(
                    Communicator.DrawMode.WireframeOnShaded
                  )
                }
                title="Solid With Edges"
              />
              <IconShaded
                onClick={() =>
                  viewer.view.setDrawMode(Communicator.DrawMode.Shaded)
                }
                title="Solid Without Edges"
              />
              <IconHiddenLine
                onClick={() =>
                  viewer.view.setDrawMode(Communicator.DrawMode.HiddenLine)
                }
                title="Ghosted"
              />
              <IconWireframe
                onClick={() =>
                  viewer.view.setDrawMode(Communicator.DrawMode.Wireframe)
                }
                title="Wireframe"
              />
            </Popover>
          )}
        </NavItem>
        <NavItem>
          <IconCuttingPlane
            id="popoverCuttingPlane"
            onClick={() => this.toggleSubmenu("cuttingPlane")}
            title="Section Planes"
          />
          {this.state.cuttingPlaneSubmenuOpen && (
            <Popover index={3}>
              <PopoverArrow top="55px" />
              <Flex>
                <CuttingPlaneButton
                  icon="-341px -257px"
                  element={cuttingPlaneBtn.planeX}
                  onClick={() => {
                    this.cuttingPlaneButtonClick("x");
                  }}
                  title="Create X-Axis Section Plane"
                />
                <CuttingPlaneButton
                  icon="-341px -341px"
                  element={cuttingPlaneBtn.visibility}
                  onClick={() => {
                    this.cuttingPlaneButtonClick("visibility");
                  }}
                  title="Hide Section Planes"
                />
              </Flex>
              <Flex>
                <CuttingPlaneButton
                  icon="-341px -215px"
                  element={cuttingPlaneBtn.planeY}
                  onClick={() => {
                    this.cuttingPlaneButtonClick("y");
                  }}
                  title="Create Y-Axis Section Plane"
                />
                <CuttingPlaneButton
                  icon="-341px -299px"
                  element={cuttingPlaneBtn.section}
                  onClick={() => {
                    this.cuttingPlaneButtonClick("section");
                  }}
                  title="Show Section Planes"
                />
              </Flex>
              <Flex>
                <CuttingPlaneButton
                  icon="-341px -173px"
                  element={cuttingPlaneBtn.planeZ}
                  onClick={() => {
                    this.cuttingPlaneButtonClick("z");
                  }}
                  title="Create Z-Axis Section Plane"
                />
                <CuttingPlaneButton
                  icon="-299px -341px"
                  element={cuttingPlaneBtn.reset}
                  onClick={() => {
                    this.cuttingPlaneButtonClick("reset");
                  }}
                  title="Remove All Section Planes"
                />
              </Flex>
            </Popover>
          )}
        </NavItem>
        {/* <NavItem>
          <IconMeasurementToolsFaceToFaceDistance
            id="measurementTools"
            onClick={() => this.toggleSubmenu("measurementTools")}
            title="Measurement tools"
          />
          {this.state.measurementToolsSubmenuOpen && (
            <Popover index={4}>
              <PopoverArrow top="65px" />
              <IconMeasurementToolsEdgeLength
                onClick={() =>
                  this.selectMeasurementTool(
                    viewer,
                    Communicator.OperatorId.MeasureEdgeLength
                  )
                }
                selected={
                  this.state.selectedMeasurementTool ===
                  Communicator.OperatorId.MeasureEdgeLength
                }
                title="Edge length"
              />
              <IconMeasurementToolsFaceToFaceDistance
                onClick={() =>
                  this.selectMeasurementTool(
                    viewer,
                    Communicator.OperatorId.MeasureFaceFaceDistance
                  )
                }
                selected={
                  this.state.selectedMeasurementTool ===
                  Communicator.OperatorId.MeasureFaceFaceDistance
                }
                title="Face to Face distance"
              />
              <IconMeasurementToolsFaceToFaceAngle
                onClick={() =>
                  this.selectMeasurementTool(
                    viewer,
                    Communicator.OperatorId.MeasureFaceFaceAngle
                  )
                }
                selected={
                  this.state.selectedMeasurementTool ===
                  Communicator.OperatorId.MeasureFaceFaceAngle
                }
                title="Face to Face angle"
              />
              <IconRemoveAllMeasurements
                onClick={() => viewer.measureManager.removeAllMeasurements()}
                title="Remove all measurements"
              />
            </Popover>
          )}
        </NavItem> */}
      </StyledToolbar>
    );
  }
}

export default Toolbar;
